import {useState, useEffect, useRef} from 'react'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import {Typography, Box, Skeleton} from '@mui/material'
import {useGlobalStates} from '../../../../contexts/GlobalStatesContext'
import {useMobile} from '../../../../hooks'
import {scheduleApi} from '../../../../services'
import {useParams} from 'react-router-dom'
import {parseError, formatTime} from '../../../../utils/helpers'
import {SkeletonContainer, TextSkeleton, ButtonSkeleton} from '../../../../components'

// [day column, first slot, slot count] for the placeholder events in the week grid.
const skeletonEvents = [
    [0, 1, 3],
    [1, 4, 2],
    [2, 0, 3],
    [3, 6, 3],
    [4, 2, 2],
    [5, 7, 3],
    [6, 3, 2],
]

const CalendarSkeleton = () => (
    <SkeletonContainer>
        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3}}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                <Skeleton variant="circular" width={24} height={24} />
                <Skeleton variant="circular" width={24} height={24} />
                <ButtonSkeleton width={80} sx={{ml: 1.5}} />
            </Box>
            <TextSkeleton width={160} />
            <Box sx={{display: 'flex'}}>
                <Skeleton variant="rounded" width={180} height={36} />
            </Box>
        </Box>
        <Box sx={{border: '1px solid #ddd'}}>
            <Box sx={{display: 'flex', height: 40, borderBottom: '1px solid #ddd'}}>
                <Box sx={{width: 56, flexShrink: 0}} />
                {Array.from({length: 7}).map((_, index) => (
                    <Box key={index} sx={{flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', borderLeft: '1px solid #ddd'}}>
                        <TextSkeleton width={56} fontSize={12} />
                    </Box>
                ))}
            </Box>
            <Box sx={{display: 'flex'}}>
                <Box sx={{width: 56, flexShrink: 0}}>
                    {Array.from({length: 12}).map((_, index) => (
                        <Box
                            key={index}
                            sx={{height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', borderBottom: '1px solid #ddd'}}>
                            {index % 2 === 0 && <TextSkeleton width={32} fontSize={10} />}
                        </Box>
                    ))}
                </Box>
                {Array.from({length: 7}).map((_, day) => {
                    const [, start, length] = skeletonEvents[day]
                    return (
                        <Box key={day} sx={{flex: 1, position: 'relative', borderLeft: '1px solid #ddd'}}>
                            {Array.from({length: 12}).map((_, index) => (
                                <Box key={index} sx={{height: 36, borderBottom: '1px solid #ddd'}} />
                            ))}
                            <Skeleton
                                variant="rectangular"
                                sx={{position: 'absolute', top: start * 37 + 2, left: 2, right: 2, height: length * 37 - 4, borderRadius: 0.5}}
                            />
                        </Box>
                    )
                })}
            </Box>
        </Box>
    </SkeletonContainer>
)

export default ({search, statuses, selectedStatuses, startDate, endDate, calendarView, setVisitStats}) => {
    const {setMessageBox, branchId} = useGlobalStates()
    const {clientId} = useParams()
    const {mobile} = useMobile()
    const [events, setEvents] = useState([])
    const [skeleton, setSkeleton] = useState(true)
    const requestId = useRef(0)

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (calendarView) {
                loadData()
            }
        }, 1000)
        return () => clearTimeout(timeout)
    }, [branchId, search, selectedStatuses, startDate, endDate, mobile, calendarView])

    const loadData = () => {
        const id = ++requestId.current
        setSkeleton(true)
        scheduleApi
            .getClientVisits(branchId, clientId, {
                page: 1,
                page_size: 100,
                search: search,
                sort_by: null,
                sort_order: null,
                status: selectedStatuses?.length === statuses?.length ? null : selectedStatuses?.join(','),
                client_ids: clientId,
                date_from: startDate?.format('YYYY-MM-DD'),
                date_to: endDate?.format('YYYY-MM-DD'),
            })
            .then(({data: {visits, status_counts}}) => {
                if (id !== requestId.current) return
                setEvents(
                    visits?.map(item => ({
                        ...item,
                        start: item?.date?.slice(0, 11) + item?.start_time,
                        end: item?.date?.slice(0, 11) + item?.end_time,
                    })),
                )
                setVisitStats(status_counts || {})
                setSkeleton(false)
            })
            .catch(error => {
                if (id !== requestId.current) return
                setVisitStats(prev => prev || {})
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
                setSkeleton(false)
            })
    }

    const renderEventContent = eventInfo => {
        return (
            <div style={{display: 'flex', flexDirection: 'column', gap: 6, padding: 6, backgroundColor: '#f7faf7'}}>
                <Typography variant="h10" color="#556950">
                    {eventInfo?.event?.extendedProps?.schedule?.service?.name}
                </Typography>
                <Typography variant="body3" color="text.secondary">
                    {eventInfo?.event?.extendedProps?.caregiver?.full_name}
                </Typography>
                <Typography variant="body3" color="#99BD90" style={{fontSize: 8}}>
                    {formatTime(eventInfo?.event?.extendedProps?.start_time)}-{formatTime(eventInfo?.event?.extendedProps?.end_time)}
                </Typography>
            </div>
        )
    }

    return (
        <>
            {skeleton && <CalendarSkeleton />}
            <div style={{display: skeleton ? 'none' : 'block'}}>
                <FullCalendar
                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                    initialView="timeGridWeek"
                    headerToolbar={{
                        left: 'prev,next,today',
                        center: 'title',
                        right: 'dayGridMonth,timeGridWeek,timeGridDay',
                    }}
                    events={events}
                    selectable={false}
                    editable={false}
                    allDaySlot={false}
                    eventContent={renderEventContent}
                />
            </div>
        </>
    )
}
